import { memo, useCallback, useMemo } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import * as yup from 'yup';

import { dialog, dom } from 'app/utils';
import { Breadcrumb } from 'app/components';
import { Button } from 'app/components/common';
import { Form } from 'app/components/layout';
import { Collection, Field, Item, Select, TextBox } from 'app/components/form';
import { users } from 'app/services';
import { IUser } from 'app/models';

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const controller = useFormik<IUser>({
    initialValues: (useLoaderData() ?? {}) as IUser,
    validationSchema: useMemo(() => yup.object({
      username: yup.string().min(5).max(255).required(),
      status: yup.number().oneOf([0, 1]).required(),
      password: yup.string().min(6),
      confirm: yup.string().oneOf([yup.ref('password')]),

      informations: yup.array().of(yup.object({
        type: yup.number().oneOf([1, 2]).required(),
        value: yup.string().required(),
      })),
    }), []),

    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: true,

    onSubmit: () => { },
  }), { handleSubmit, handleReset } = controller;

  const onSave = () => {
    if (!controller.isValid) { dialog.toast({ message: t('app.validation.required'), severity: 'error' }); return; }

    users.save(controller.values).then(({ success, data }) => {
      if (!success) return;

      dialog.toast({ severity: 'success', message: t('users.save.success') });
      navigate(`../${data.id}`);
    });
  };

  const { touched, errors } = controller;
  const getProps = useCallback((field: string): any => ({ ...controller.getFieldProps(field),
    invalid: !!(touched[field as keyof FormikTouched<IUser>] && errors[field as keyof FormikErrors<IUser>]),
  }), [controller, touched, errors]);

  const form: any = {
    className: dom.className('p-4', 'block', 'flex-auto', 'overflow-auto'),

    onSubmit: handleSubmit,
    onReset: handleReset,
  };

  return (
    <div className="h-full flex flex-column align-items-stretch">
      <div className="flex-none flex surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text severity="warning" href=".." icon="pi-times" label={t('app.cancel')} className="mr-2 px-3 py-1" />
          <Button icon="mdi-check" label={t('app.save')} className="px-3 py-1" onClick={onSave} />
        </div>
      </div>
      <form { ...form }>
        <Form responsive={true}>
          <Field text={t('users.fields.username')} error={controller.errors.username} align="end" asterisk>
            <TextBox {...getProps('username')} />
          </Field>
          <Field text={t('users.fields.status')} error={controller.errors.status} align="end" asterisk className="pt-4">
            <Select {...getProps('status')} options={[
              { label: t('users.fields.status.disabled'), value: 0 },
              { label: t('users.fields.status.enabled'), value: 1 },
              { label: t('users.fields.status.approved'), value: 2 },
            ]}/>
          </Field>
          <Field text={t('users.fields.password')} error={controller.errors.password} align="end" asterisk className="pt-4">
            <TextBox {...getProps('password')} placeholder={t('users.fields.password.placeholder')}/>
          </Field>
          <Field text={t('users.fields.confirm')} error={controller.errors.confirm} align="end" asterisk className="pt-4">
            <TextBox {...getProps('confirm')} placeholder={t('users.fields.password.placeholder')} />
          </Field>
          <Field text={t('users.fields.informations')} error={controller.errors.username} align="end" asterisk className="pt-4">
            <Collection template={(item, i) => (
              <Item value={item} onRemove={(_) => { }}>
                <Select {...getProps(`informations[${i}].type`)} options={[
                  { label: t('app.info.email'), value: 1 },
                  { label: t('app.info.phone'), value: 2 },
                ]} className="w-8rem border-0"/>
                <TextBox {...getProps(`informations[${i}].value`)} className="flex-auto border-0"/>
              </Item>
            )} />
          </Field>
        </Form>
      </form>
    </div>
  );
});
